/**
 * useExtensionDetection
 *
 * Detects whether the Enter Control Chrome extension is installed and active
 * in the current browser tab.
 *
 * How detection works:
 * 1. extension-listener.ts sends `SPA_READY` on page load.
 * 2. The extension's content script receives it and replies with `EXTENSION_READY`.
 * 3. extension-listener.ts sets `window.__ENTER_CONTROL_EXTENSION__ = true` and
 * dispatches the `'extension-ready'` DOM event.
 * 4. This hook observes that event (or the flag if already set) to confirm presence.
 * 5. After DETECTION_TIMEOUT_MS with no reply, the extension is considered absent.
 *
 * Returns:
 * - `isDetecting` — true while waiting for the extension to respond
 * - `hasExtension` — true once EXTENSION_READY has been received
 * - `isMobile` — true when the browser cannot support a Chrome extension at all
 */

import { useEffect, useState } from 'react';

const DETECTION_TIMEOUT_MS = 2500;

function isMobileBrowser(): boolean {
  return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
}

function isExtensionAlreadyDetected(): boolean {
  // Set by extension-listener.ts when EXTENSION_READY is received
  return !!(window as unknown as Record<string, unknown>)['__ENTER_CONTROL_EXTENSION__'];
}

export type ExtensionDetectionResult = {
  /** Still waiting for the extension to respond */
  isDetecting: boolean;
  /** Extension replied — it is installed and active */
  hasExtension: boolean;
  /** Browser cannot run Chrome extensions (mobile, Firefox, Safari, etc.) */
  isMobile: boolean;
};

export const useExtensionDetection = (): ExtensionDetectionResult => {
  const mobile = isMobileBrowser();
  const [hasExtension, setHasExtension] = useState(isExtensionAlreadyDetected);
  const [isDetecting, setIsDetecting] = useState(() => !isExtensionAlreadyDetected() && !mobile);

  useEffect(() => {
    // On mobile there is nothing to detect
    if (mobile) return;
    // Already confirmed — nothing to do
    if (hasExtension) return;

    const handleReady = () => {
      setHasExtension(true);
      setIsDetecting(false);
    };

    window.addEventListener('extension-ready', handleReady);

    const timeout = setTimeout(() => {
      setIsDetecting(false);
    }, DETECTION_TIMEOUT_MS);

    return () => {
      window.removeEventListener('extension-ready', handleReady);
      clearTimeout(timeout);
    };
  }, [hasExtension, mobile]);

  return { isDetecting, hasExtension, isMobile: mobile };
};
